/**
 * Text Formatting Utilities
 *
 * Centralized markdown parsing and formatting for text content.
 * Handles bold (**), italic (*), strikethrough (~~), highlight (==), links,
 * and header (#) syntax.
 */

import type { ReactNode } from "react";
import { authoredPlaces, CountUpNumber } from "../components/CountUpNumber";
import { HighlightSpan } from "../components/HighlightSpan";
import { NUMBER_PILL_CLASS, numberPillStyle } from "./numberPill";

/**
 * Parse markdown header syntax (# - ######)
 * Returns null if text is not a header
 */
export function parseHeader(
  text: string,
): { level: number; content: string } | null {
  const match = text.match(/^(#{1,6})\s+(.+)$/);
  if (match) {
    return { level: match[1].length, content: match[2] };
  }
  return null;
}

/**
 * Get CSS classes for header styling based on level
 */
export function getHeaderClassName(level: number): string {
  switch (level) {
    case 1:
      return "block text-3xl font-bold font-serif mb-5";
    case 2:
      return "block text-2xl font-semibold font-serif mb-4";
    case 3:
      return "block text-xl font-semibold font-serif mb-3";
    case 4:
      return "block text-lg font-semibold mb-2";
    case 5:
      return "block text-base font-semibold mb-1";
    case 6:
      return "block text-sm font-semibold";
    default:
      return "";
  }
}

/**
 * Strip all markdown formatting markers for character counting
 */
export function stripMarkdownSyntax(text: string): string {
  // Remove headers (# symbols at start of line)
  let stripped = text.replace(/^#{1,6}\s+/gm, "");
  // With a highlight present, count what the parser renders: it and these
  // regexes disagree on precedence when a highlight straddles `*italic*` or
  // `**bold**`, which would end the typewriter before the text does.
  if (stripped.includes("==")) {
    const segments = parseInlineMarkdown(stripped);
    if (segments.some((seg) => seg.highlight)) {
      return segments.map((seg) => seg.content).join("");
    }
  }
  // Replace [text](url) links with just the text
  stripped = stripped.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  // Remove ~~, **, and * markers
  stripped = stripped
    .replace(/~~/g, "")
    .replace(/\*\*/g, "")
    .replace(/\*/g, "");
  return stripped;
}

/** A complete `[text](url)` link — matched whole wherever a pattern must not read inside one. */
const LINK_SRC = String.raw`\[[^\]]+?\]\([^)]+?\)`;

/**
 * `{#RGB}`, `{#RGBA}`, `{#RRGGBB}` or `{#RRGGBBAA}` straight after a
 * highlight's closing `==`. Hex only: the value ends up in a style attribute,
 * and a bar's colour is a hex to match against.
 */
const HIGHLIGHT_COLOR_SRC = String.raw`\{#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{4}|[0-9a-fA-F]{3})\}`;

/**
 * `==phrase==`, optionally followed by a `{#hex}` colour.
 *
 * Written so prose that merely contains `==` stays literal: the phrase must
 * start and end on a non-space (`a == b == c` is not a highlight), a run of
 * three or more `=` never delimits one, and an unclosed `==` matches nothing.
 * It stays on one line, and a link inside it is consumed whole, so a `==` in a
 * URL (base64 padding) can neither close the phrase nor start one.
 *
 * Keep in sync with `lintHighlightMarkup` (apps/forward/src/utils/lintNarrative.ts),
 * which re-implements this grammar and the colour format to warn authors; if
 * either changes, change both and their tests.
 */
const HIGHLIGHT_SRC = String.raw`(?<!=)==(?![\s=])(?:${LINK_SRC}|(?!${LINK_SRC})(?!==)[^\n])+?(?<![\s=])==(?!=)(?:${HIGHLIGHT_COLOR_SRC})?`;

const HIGHLIGHT_EXACT_RE = new RegExp(`^${HIGHLIGHT_SRC}$`);
const HIGHLIGHT_COLOR_SUFFIX_RE = new RegExp(`(${HIGHLIGHT_COLOR_SRC})$`);

/** Splits inside bold/italic/strikethrough text, keeping links and highlights whole. */
const HIGHLIGHT_OR_LINK_RE = new RegExp(`(${HIGHLIGHT_SRC}|${LINK_SRC})`);

/**
 * Shared regex that splits text into markdown segments (bold+italic, bold,
 * italic, strikethrough, highlight, link, newline, plain).
 */
const INLINE_MARKDOWN_RE = new RegExp(
  String.raw`(\*\*\*[^*]+?\*\*\*|\*\*[^*]+?\*\*|\*(?!\*)[^*]+?\*(?!\*)|~~[^~]+?~~|${HIGHLIGHT_SRC}|${LINK_SRC}|\n)`,
  "g",
);

/** Split a matched `==phrase==` (+ optional `{#hex}`) into its phrase and colour; null if it is not one. */
function parseHighlightToken(
  token: string,
): { content: string; color?: string } | null {
  if (!token.startsWith("==") || !HIGHLIGHT_EXACT_RE.test(token)) return null;
  const suffix = token.match(HIGHLIGHT_COLOR_SUFFIX_RE)?.[1];
  const content = token.slice(2, token.length - (suffix?.length ?? 0) - 2);
  return suffix ? { content, color: suffix.slice(1, -1) } : { content };
}

export type InlineSegmentKind =
  | "bold-italic"
  | "bold"
  | "italic"
  | "strikethrough"
  | "link"
  | "newline"
  | "plain";

/** One `==highlight==` span. Every segment inside it carries the same object. */
export interface InlineHighlight {
  /** Unique per parse, so renderers can wrap a run of segments as one element. */
  id: number;
  /** Hex from a `{#hex}` suffix, leading "#" included; absent for the section default. */
  color?: string;
}

export interface InlineSegment {
  kind: InlineSegmentKind;
  raw: string;
  /** Text content with markdown markers stripped. */
  content: string;
  /** URL for link segments. */
  url?: string;
  /** Inner formatting kind for link segments (e.g. italic in [*text*](url)). */
  innerKind?: InlineSegmentKind;
  /** Outer formatting kind when a link appears inside bold/italic (e.g. bold in **[text](url)**). */
  outerKind?: InlineSegmentKind;
  /**
   * Set when the segment sits inside a `==highlight==`. Highlights nest with
   * the other syntaxes the way links do — as an attribute on the segments
   * inside, not as a segment of their own — so `==grew **42%** overall==` is
   * three segments sharing one highlight, and `**a ==b== c**` is three bold
   * segments of which the middle one is highlighted.
   */
  highlight?: InlineHighlight;
}

const LINK_RE = new RegExp(`(${LINK_SRC})`, "g");

const SAFE_URL_RE = /^(https?:\/\/|mailto:|tel:|\/|#)/i;
function sanitizeUrl(url: string): string | undefined {
  const trimmed = url.trim();
  return SAFE_URL_RE.test(trimmed) ? trimmed : undefined;
}

/**
 * Parse a string into inline markdown segments.
 * Single source of truth for bold+italic / bold / italic detection.
 */
export function parseInlineMarkdown(text: string): InlineSegment[] {
  return parseInline(text, { next: 0 });
}

/** Hands out highlight ids, so every span in one parse is distinguishable. */
interface HighlightIds {
  next: number;
}

function makeHighlight(
  ids: HighlightIds,
  color: string | undefined,
): InlineHighlight {
  return color ? { id: ids.next++, color } : { id: ids.next++ };
}

/**
 * @param inside Set while parsing the phrase of a `==highlight==`, so every
 *   segment found in it is marked as belonging to that highlight.
 */
function parseInline(
  text: string,
  ids: HighlightIds,
  inside?: InlineHighlight,
): InlineSegment[] {
  const out: InlineSegment[] = [];
  for (const part of text.split(INLINE_MARKDOWN_RE)) {
    if (part === "" || part === undefined) continue;

    const highlight = parseHighlightToken(part);
    if (highlight) {
      const span = makeHighlight(ids, highlight.color);
      out.push(...parseInline(highlight.content, ids, span));
      continue;
    }

    const seg = segmentFromPart(part);
    if (inside) seg.highlight = inside;
    out.push(...expandWrapped(seg, ids));
  }
  return out;
}

/** First pass: classify one piece of the split text. */
function segmentFromPart(part: string): InlineSegment {
  if (part === "\n") {
    return { kind: "newline", raw: part, content: part };
  }
  if (part.startsWith("***") && part.endsWith("***")) {
    return { kind: "bold-italic", raw: part, content: part.slice(3, -3) };
  }
  if (part.startsWith("**") && part.endsWith("**")) {
    return { kind: "bold", raw: part, content: part.slice(2, -2) };
  }
  if (part.startsWith("*") && part.endsWith("*") && !part.startsWith("**")) {
    return { kind: "italic", raw: part, content: part.slice(1, -1) };
  }
  if (part.startsWith("~~") && part.endsWith("~~")) {
    return { kind: "strikethrough", raw: part, content: part.slice(2, -2) };
  }
  const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
  if (linkMatch) {
    let linkText = linkMatch[1];
    let innerKind: InlineSegmentKind | undefined;
    if (linkText.startsWith("***") && linkText.endsWith("***")) {
      innerKind = "bold-italic";
      linkText = linkText.slice(3, -3);
    } else if (linkText.startsWith("**") && linkText.endsWith("**")) {
      innerKind = "bold";
      linkText = linkText.slice(2, -2);
    } else if (
      linkText.startsWith("*") &&
      linkText.endsWith("*") &&
      !linkText.startsWith("**")
    ) {
      innerKind = "italic";
      linkText = linkText.slice(1, -1);
    } else if (linkText.startsWith("~~") && linkText.endsWith("~~")) {
      innerKind = "strikethrough";
      linkText = linkText.slice(2, -2);
    }
    const safeUrl = sanitizeUrl(linkMatch[2]);
    if (!safeUrl) return { kind: "plain", raw: part, content: linkText };
    return {
      kind: "link",
      raw: part,
      content: linkText,
      url: safeUrl,
      innerKind,
    };
  }
  return { kind: "plain", raw: part, content: part };
}

/**
 * Second pass: expand highlights and links written inside
 * bold/italic/strikethrough segments.
 */
function expandWrapped(seg: InlineSegment, ids: HighlightIds): InlineSegment[] {
  if (
    seg.kind !== "bold" &&
    seg.kind !== "italic" &&
    seg.kind !== "bold-italic" &&
    seg.kind !== "strikethrough"
  ) {
    return [seg];
  }
  const pieces = seg.content.includes("==")
    ? seg.content.split(HIGHLIGHT_OR_LINK_RE)
    : [];
  if (pieces.length > 1) {
    const expanded: InlineSegment[] = [];
    for (const piece of pieces) {
      if (!piece) continue;
      const parsed = parseHighlightToken(piece);
      const content = parsed?.content ?? piece;
      const highlight = parsed
        ? makeHighlight(ids, parsed.color)
        : seg.highlight;
      expanded.push(
        ...expandLinks({
          kind: seg.kind,
          raw: content,
          content,
          ...(highlight && { highlight }),
        }),
      );
    }
    return expanded;
  }
  return expandLinks(seg);
}

/** Expand links written inside a bold/italic/strikethrough segment. */
function expandLinks(seg: InlineSegment): InlineSegment[] {
  if (!seg.content.includes("[")) return [seg];
  const innerParts = seg.content.split(LINK_RE);
  if (innerParts.length <= 1) return [seg];

  const carry = seg.highlight ? { highlight: seg.highlight } : {};
  const expanded: InlineSegment[] = [];
  for (const inner of innerParts) {
    if (!inner) continue;
    const lm = inner.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (lm) {
      const safeUrl = sanitizeUrl(lm[2]);
      if (safeUrl) {
        expanded.push({
          kind: "link",
          raw: inner,
          content: lm[1],
          url: safeUrl,
          outerKind: seg.kind,
          ...carry,
        });
      } else {
        expanded.push({ kind: seg.kind, raw: lm[1], content: lm[1], ...carry });
      }
    } else {
      expanded.push({ kind: seg.kind, raw: inner, content: inner, ...carry });
    }
  }
  return expanded;
}

/** A bold figure that counts up: `$1,280`, `62.9%`, `$1.2M`. */
const NUMBER_RE = /^(\$)?(\d{1,3}(?:,\d{3})*(?:\.\d+)?)(M|K|%)?$/;
const STARTS_WITH_NUMBER_RE = /^(\$)?\d/;

/**
 * Render each segment with `renderSegment`, wrapping every run of segments that
 * share a `==highlight==` in a single HighlightSpan. One wrapper per highlight
 * is what makes the marker one continuous stroke, however much bold, link or
 * number formatting sits inside it.
 */
export function renderHighlightRuns(
  segments: InlineSegment[],
  renderSegment: (seg: InlineSegment, index: number) => ReactNode,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let i = 0;
  while (i < segments.length) {
    const highlight = segments[i].highlight;
    if (!highlight) {
      nodes.push(renderSegment(segments[i], i));
      i++;
      continue;
    }
    const run: ReactNode[] = [];
    let seed = "";
    while (i < segments.length && segments[i].highlight?.id === highlight.id) {
      run.push(renderSegment(segments[i], i));
      seed += segments[i].content;
      i++;
    }
    nodes.push(
      <HighlightSpan
        key={`highlight-${highlight.id}`}
        color={highlight.color}
        seed={seed}
      >
        {run}
      </HighlightSpan>,
    );
  }
  return nodes;
}

/**
 * Render inline markdown formatting (bold, italic, highlight) with optional number animations
 */
export function renderInlineFormatting(
  text: string,
  delay: number = 0,
): ReactNode[] {
  const segments = parseInlineMarkdown(text);

  return renderHighlightRuns(segments, (seg, i) => {
    if (seg.kind === "bold-italic") {
      return (
        <span
          key={i}
          className="font-bold italic"
          style={{ fontStyle: "italic" }}
        >
          {seg.content}
        </span>
      );
    }

    if (seg.kind === "bold") {
      // Check if bold text contains numbers to animate
      const match = seg.content.match(NUMBER_RE);

      if (match) {
        const prefix = match[1] || "";
        const numberStr = match[2];
        const suffix = match[3] || "";
        const numericValue = parseFloat(numberStr.replace(/,/g, ""));

        // Animate numbers that are 10 or greater
        if (numericValue >= 10) {
          return (
            <span
              key={i}
              className={NUMBER_PILL_CLASS}
              style={numberPillStyle(seg.content)}
            >
              {/*
                Prefix and suffix are handed over as props rather than folded
                into a `format` callback so the value goes through
                CountUpNumber's own formatter, which holds the fraction digits
                on every frame instead of dropping a trailing zero mid-count —
                a "$1.2M" that passes through "$1M" is two characters narrower
                and re-flows the text around it.

                fractionDigits comes from the authored text, not from
                numericValue: parseFloat has already turned "45.60" into 45.6
                by here, and a price written in cents should render in cents —
                as it does when the same value sits below the animation gate
                and is rendered verbatim.
              */}
              <CountUpNumber
                value={numericValue}
                delay={delay + 0.3}
                prefix={prefix}
                suffix={suffix}
                fractionDigits={authoredPlaces(numberStr)}
              />
            </span>
          );
        }
      }

      // Not a number or too small - just render bold (check if it's a number for special styling)
      const isNumber = STARTS_WITH_NUMBER_RE.test(seg.content);
      return (
        <span
          key={i}
          className={isNumber ? NUMBER_PILL_CLASS : "font-bold"}
          style={isNumber ? numberPillStyle(seg.content) : undefined}
        >
          {seg.content}
        </span>
      );
    }

    if (seg.kind === "italic") {
      return (
        <span
          key={i}
          className="italic"
          style={{ fontStyle: "italic", opacity: 0.95 }}
        >
          {seg.content}
        </span>
      );
    }

    if (seg.kind === "strikethrough") {
      return (
        <span
          key={i}
          className="line-through"
          style={{ textDecoration: "line-through" }}
        >
          {seg.content}
        </span>
      );
    }

    if (seg.kind === "link" && seg.url) {
      const effectiveKind = seg.innerKind || seg.outerKind;
      const styleClass =
        effectiveKind === "bold-italic"
          ? "font-bold italic"
          : effectiveKind === "bold"
            ? "font-bold"
            : effectiveKind === "italic"
              ? "italic"
              : effectiveKind === "strikethrough"
                ? "line-through"
                : undefined;
      const styleObj =
        effectiveKind === "bold-italic" || effectiveKind === "italic"
          ? { fontStyle: "italic" as const }
          : undefined;
      return (
        <a
          key={i}
          href={seg.url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 decoration-1 hover:opacity-80 transition-opacity"
        >
          {styleClass ? (
            <span className={styleClass} style={styleObj}>
              {seg.content}
            </span>
          ) : (
            seg.content
          )}
        </a>
      );
    }

    return <span key={i}>{seg.content}</span>;
  });
}

/**
 * Render text with full markdown support (headers + inline formatting)
 */
export function renderTextWithFormatting(
  text: string,
  delay: number = 0,
): ReactNode {
  // Check for markdown header at the start
  const header = parseHeader(text);
  if (header) {
    // Render header content with inner formatting (bold/italic)
    const innerContent = renderInlineFormatting(header.content, delay);
    return (
      <span className={getHeaderClassName(header.level)}>{innerContent}</span>
    );
  }

  // Otherwise, render with inline formatting
  return renderInlineFormatting(text, delay);
}

/**
 * Temporarily protect markdown markers from phrase splitting.
 * Uses INLINE_MARKDOWN_RE so all syntaxes recognised by parseInlineMarkdown()
 * (bold-italic, bold, italic, strikethrough) are shielded consistently.
 */
function protectMarkdownMarkers(text: string): {
  protected: string;
  restore: (s: string) => string;
} {
  const placeholders: string[] = [];

  // Reuse INLINE_MARKDOWN_RE to protect every recognised markdown span
  const protected_ = text.replace(INLINE_MARKDOWN_RE, (match) => {
    if (match === "\n") return match; // Don't protect newlines
    const index = placeholders.length;
    placeholders.push(match);
    return `${index}`; // Use private use area characters as delimiters
  });

  // Function to restore placeholders
  const restore = (s: string): string => {
    return s.replace(
      /(\d+)/g,
      (_, index) => placeholders[parseInt(index, 10)],
    );
  };

  return { protected: protected_, restore };
}

/**
 * Split text into phrases for animation purposes
 * Phrases are split by: commas, em-dashes, sentence boundaries, and newlines
 * Returns array with phrase text and newline information
 */
export function splitIntoPhrases(
  text: string,
): { text: string; endsWithNewline: boolean }[] {
  const result: { text: string; endsWithNewline: boolean }[] = [];

  // First split by newlines to preserve them
  const lines = text.split(/(\n+)/);

  for (let i = 0; i < lines.length; i++) {
    const segment = lines[i];

    // Skip empty strings
    if (!segment) continue;

    // Check if this is a newline segment
    if (/^\n+$/.test(segment)) {
      // Mark the previous phrase as ending with newline
      if (result.length > 0) {
        result[result.length - 1].endsWithNewline = true;
      }
      continue;
    }

    // If line starts with header marker, keep entire line as one phrase (don't split on periods)
    if (/^#{1,6}\s+/.test(segment)) {
      result.push({
        text: segment,
        endsWithNewline: false,
      });
      continue;
    }

    // Protect markdown markers before splitting to avoid breaking **bold, text** patterns
    const { protected: protectedSegment, restore } =
      protectMarkdownMarkers(segment);

    // Split this line into phrases
    const phrases = protectedSegment
      .split(/(?<=,)\s+(?=[A-Za-z])|(?<=—)\s*|(?<=[.!?])\s+/)
      .filter((phrase) => phrase.trim().length > 0);

    phrases.forEach((phrase) => {
      result.push({
        text: restore(phrase), // Restore markdown markers
        endsWithNewline: false,
      });
    });
  }

  return result;
}

/**
 * Split text into individual words for word-level animation effects.
 * Markdown spans (bold, italic, strikethrough) are kept whole as a single word token.
 * Newlines are tracked via the endsWithNewline flag on the preceding word.
 */
export function splitIntoWords(
  text: string,
): { text: string; endsWithNewline: boolean }[] {
  const result: { text: string; endsWithNewline: boolean }[] = [];

  // Split by newlines first, preserving them
  const lines = text.split(/(\n+)/);

  for (const segment of lines) {
    if (!segment) continue;

    // Newline segments mark the previous word as line-ending
    if (/^\n+$/.test(segment)) {
      if (result.length > 0) {
        result[result.length - 1].endsWithNewline = true;
      }
      continue;
    }

    // Protect markdown spans so **bold text** stays as one token
    const { protected: protectedSegment, restore } =
      protectMarkdownMarkers(segment);

    // Split on whitespace runs
    const rawWords = protectedSegment.split(/\s+/).filter((w) => w.length > 0);

    rawWords.forEach((word) => {
      result.push({ text: restore(word), endsWithNewline: false });
    });
  }

  return result;
}
